export interface RegistrationResponseField {
  name: string;
  label: string;
  type?: string;
  order?: number;
  options?: { label: string; value: string }[];
}

export interface PresentedRegistrationResponse {
  key: string;
  label: string;
  text: string;
  multiline: boolean;
}

function humanizeRegistrationFieldKey(key: string): string {
  const spaced = key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
  return spaced.replace(/\b\w/g, (char) => char.toUpperCase());
}

function optionLabel(options: { label: string; value: string }[] | undefined, raw: string): string {
  const match = options?.find((option) => option.value === raw);
  return match?.label?.trim() || raw;
}

function formatResponseValue(
  value: unknown,
  options: { label: string; value: string }[] | undefined
): { text: string; multiline: boolean } {
  if (value === null || value === undefined || value === "") return { text: "—", multiline: false };
  if (typeof value === "boolean") return { text: value ? "Yes" : "No", multiline: false };
  if (typeof value === "number") return { text: String(value), multiline: false };
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => (item == null ? "" : optionLabel(options, String(item).trim())))
      .map((item) => item.trim())
      .filter(Boolean);
    return { text: parts.length ? parts.join(", ") : "—", multiline: false };
  }
  if (typeof value === "object") {
    try {
      return { text: JSON.stringify(value, null, 2), multiline: true };
    } catch {
      return { text: String(value), multiline: false };
    }
  }
  const text = optionLabel(options, String(value));
  return { text, multiline: text.length > 200 && text.includes("\n") };
}

export function presentRegistrationResponses(
  formData: Record<string, unknown>,
  fields: RegistrationResponseField[]
): PresentedRegistrationResponse[] {
  const byName = new Map<string, RegistrationResponseField>();
  for (const field of fields) {
    const name = field.name?.trim();
    if (name) byName.set(name, field);
  }

  const seen = new Set<string>();
  const keys: string[] = [];
  const ordered = [...byName.values()].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  for (const field of ordered) {
    if (Object.prototype.hasOwnProperty.call(formData, field.name) && !seen.has(field.name)) {
      keys.push(field.name);
      seen.add(field.name);
    }
  }
  for (const key of Object.keys(formData)) {
    if (!seen.has(key)) keys.push(key);
  }

  return keys.map((key) => {
    const field = byName.get(key);
    const formatted = formatResponseValue(formData[key], field?.options);
    return {
      key,
      label: field?.label?.trim() || humanizeRegistrationFieldKey(key),
      text: formatted.text,
      multiline: formatted.multiline,
    };
  });
}
